// @ts-nocheck
/* eslint-disable */
import axios from 'axios';
import { toast } from 'react-toastify';

/*
 * Pre-flight guard for screens that SET a price on an axis — the base fare on
 * Edit/Add Axis, and the cash/card price of a zone.
 *
 * This is the mirror of applicableFares.tsx. That one stops a discount being
 * set above a fare; this one stops a fare being set below an existing fixed
 * discount. Same invariant either way: a fixed discount larger than the charged
 * price does not error at tap time — the pricing functions clamp with
 * LEAST(price, discount_amount) — so the trip silently becomes free.
 *
 * Neither write path is touched. Edit Axis still posts to the Ruby API and
 * zones still post to the existing /zones controller; this only runs first and
 * reports whether to continue.
 */

const API = `${process.env.REACT_APP_ABT_ALL_APIS_BASE_URL}/api/v1/web_portal/fare_media_policy_capping/fare_constraints`;

const authConfig = () => {
  let authData = null;
  const localAuthData = localStorage.getItem('authData');
  if (localAuthData) {
    authData = JSON.parse(localAuthData).userToken;
  }
  return {
    headers: {
      'access-token': authData?.['access-token'] || authData?.access_token,
      'uid': authData?.uid,
      'client': authData?.client,
      'Authorization': 'Bearer ' + localStorage.getItem('dashboard_token'),
    },
  };
};

const tr = (intl, id, fallback) => intl?.formatMessage?.({ id }) || fallback;

const bandLabel = (b, isArabic) =>
  (isArabic ? b.name : b.en_name || b.name) || b.code || '';

const report = (data, intl) => {
  const isArabic = localStorage.getItem('EASY_MOBILITY_LANGUAGE') === 'ar-eg';
  const worst = (data.blocking_bands || [])[0];
  const who = worst ? bandLabel(worst, isArabic) : '';
  toast.error(
    `${tr(intl, 'PriceBelowActiveDiscount', 'This price is lower than an active fixed discount on this axis')} ` +
    `(${tr(intl, 'MinimumAllowedPrice', 'minimum allowed')}: ${data.min_allowed_price}` +
    `${who ? ` — ${who}` : ''}).`,
    { autoClose: 9000 }
  );
};

/**
 * @returns {Promise<boolean>} true if the caller may proceed with its own save
 */
export const axisPriceAllowed = async ({
  // Add Axis has no id yet and passes only routeId; Edit Axis passes both.
  axisId = null,
  routeId = null,
  cashPrice = null,
  cardPrice = null,
  intl = null,
}: {
  axisId?: any; routeId?: any; cashPrice?: any; cardPrice?: any; intl?: any;
}) => {
  try {
    const res = await axios.post(
      `${API}/check_axis_price`,
      { axis_id: axisId || null, route_id: routeId || null, cash_price: cashPrice, card_price: cardPrice },
      authConfig()
    );
    const data = res.data.data;
    if (data.allowed) return true;
    report(data, intl);
    return false;
  } catch (e) {
    // An unreachable check must not block an edit that is probably fine — but
    // unlike the discount guard there is no server-side net behind this one, so
    // say so rather than failing silently.
    toast.warn(tr(intl, 'CouldNotVerifyDiscountUsage', 'Could not verify active discounts for this axis — saving without that check.'));
    return true;
  }
};

export const zonePriceAllowed = async ({
  fareAxisId,
  zoneCashPrice = null,
  zoneCardPrice = null,
  intl = null,
}: {
  fareAxisId: any; zoneCashPrice?: any; zoneCardPrice?: any; intl?: any;
}) => {
  try {
    const res = await axios.post(
      `${API}/check_zone_price`,
      { fareAxisId, zoneCashPrice, zoneCardPrice },
      authConfig()
    );
    const data = res.data.data;
    if (data.allowed) return true;
    report(data, intl);
    return false;
  } catch (e) {
    toast.warn(tr(intl, 'CouldNotVerifyDiscountUsage', 'Could not verify active discounts for this axis — saving without that check.'));
    return true;
  }
};
